import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnChanges,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {MatPaginator, MatPaginatorModule} from '@angular/material/paginator';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatSelectModule} from '@angular/material/select';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatNativeDateModule} from '@angular/material/core';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {debounceTime} from 'rxjs/operators';

export interface HistoryEntry {
    dt_change: { datetime: { date: string; }; };
    tx_change: string;
    ds_userchange: string;
    dt_reviewed: { datetime: { date: string; }; } | null;
    tx_review: string | null;
}

interface MappedHistoryEntry extends HistoryEntry {
    changedFields: string[];
    isReviewed: boolean;
    reviewDate: string | null;
    reviewSummary: string | null;
    reviewComment: string | null;
}

@Component({
    selector: 'app-change-history',
    standalone: true,
    imports: [
        CommonModule, ReactiveFormsModule, MatPaginatorModule, MatFormFieldModule,
        MatInputModule, MatSelectModule, MatDatepickerModule, MatNativeDateModule,
        MatButtonModule, MatIconModule
    ],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './change-history.component.html'
})
export class ChangeHistoryComponent implements OnChanges, AfterViewInit {

    @Input() history: HistoryEntry[] = [];
    @ViewChild(MatPaginator) paginator!: MatPaginator;

    fullHistory: MappedHistoryEntry[] = [];
    filteredHistory: MappedHistoryEntry[] = [];
    paginatedHistory: MappedHistoryEntry[] = [];

    uniqueUsers: string[] = [];
    filterForm: FormGroup;

    private fieldMappings: Record<string, string> = {
        'ds_name': 'Name', 'ds_label': 'Label', 'ds_title': 'Title', 'ds_shortname': 'Short Name',
        'tx_description': 'Description', 'tx_data1': 'Custom Data', 'en_state': 'State',
        'ds_googlematch': 'Google Match', 'ds_googleaddress': 'Google Address', 'vr_lat': 'Latitude',
        'vr_long': 'Longitude', 'vr_northwestlat': 'NW Corner Latitude', 'vr_northwestlong': 'NW Corner Longitude',
        'Offer Added or Edited': 'Offer', 'Products Updated': 'Products', 'Product Added': 'Products',
        'Stories Updated': 'Stories', 'Gallery Added': 'Gallery', 'bl_translationdisabletitle': 'Title Translation Setting Changed',
    };

    constructor(private fb: FormBuilder, private cdr: ChangeDetectorRef) {
        this.filterForm = this.fb.group({
            user: [''],
            keyword: [''],
            dateRange: this.fb.group({
                start: [null],
                end: [null]
            })
        });

        this.filterForm.valueChanges.pipe(
            debounceTime(300)
        ).subscribe(() => {
            this.applyFilters();
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['history'] && changes['history'].currentValue) {
            this.initializeData();
        }
    }

    ngAfterViewInit(): void {
        if (this.paginator) {
            this.paginator.page.subscribe(() => this.updatePaginatedData());
        }
    }

    private initializeData(): void {
        this.fullHistory = (this.history || [])
            .map(entry => {
                const changedFields = this.getChangedFields(entry.tx_change);
                const isReviewed = !!entry.dt_reviewed;
                let reviewDate = null;
                let reviewSummary = null;
                let reviewComment = null;

                if (isReviewed && entry.dt_reviewed && entry.tx_review) {
                    reviewDate = entry.dt_reviewed.datetime.date;
                    const reviewParts = entry.tx_review.split('<br/>\nComment:');
                    reviewSummary = reviewParts[0]?.trim();
                    if (reviewParts.length > 1) {
                        reviewComment = reviewParts[1]?.trim();
                    }
                }

                return {
                    ...entry,
                    changedFields,
                    isReviewed,
                    reviewDate,
                    reviewSummary,
                    reviewComment
                };
            })
            .sort((a, b) => new Date(b.dt_change.datetime.date).getTime() - new Date(a.dt_change.datetime.date).getTime());

        this.uniqueUsers = [...new Set(this.fullHistory.map(h => h.ds_userchange))].sort();
        this.applyFilters();
    }

    private parseUtcDate(raw?: string): Date | null {
        if (!raw) return null;
        const [d, t] = raw.split(' ');
        if (!d || !t) return null;
        const [hms, frac = ''] = t.split('.');
        const ms = frac.slice(0, 3).padEnd(3, '0');
        const iso = `${d}T${hms}.${ms}Z`;
        const dt = new Date(iso);
        return isNaN(dt.getTime()) ? null : dt;
    }

    applyFilters(): void {
        const { user, keyword, dateRange } = this.filterForm.value;

        const startDate: Date | null = dateRange.start ? new Date(dateRange.start) : null;
        const endDate: Date | null = dateRange.end ? new Date(dateRange.end) : null;
        if (endDate) {
            endDate.setHours(23, 59, 59, 999);
        }

        const lowerCaseKeyword = (keyword || '').toLowerCase();
        const lowerCaseUser = (user || '').toLowerCase();

        this.filteredHistory = this.fullHistory.filter(entry => {
            const entryDate = this.parseUtcDate(entry.dt_change.datetime.date);
            const userMatch = !lowerCaseUser || (entry.ds_userchange || '').toLowerCase() === lowerCaseUser;
            const keywordMatch = !lowerCaseKeyword ||
                (entry.ds_userchange || '').toLowerCase().includes(lowerCaseKeyword) ||
                (entry.tx_change || '').toLowerCase().includes(lowerCaseKeyword) ||
                entry.changedFields.some(field => field.toLowerCase().includes(lowerCaseKeyword)) ||
                (entry.reviewSummary || '').toLowerCase().includes(lowerCaseKeyword) ||
                (entry.reviewComment || '').toLowerCase().includes(lowerCaseKeyword);
            const startDateMatch = !startDate || (entryDate && entryDate >= startDate);
            const endDateMatch = !endDate || (entryDate && entryDate <= endDate);

            return userMatch && keywordMatch && startDateMatch && endDateMatch;
        });

        if (this.paginator) {
            this.paginator.firstPage();
        }

        this.updatePaginatedData();
        this.cdr.markForCheck();
    }

    resetFilters(): void {
        this.filterForm.reset({
            user: '',
            keyword: '',
            dateRange: { start: null, end: null }
        });
    }

    private updatePaginatedData(): void {
        if (!this.paginator) {
            const startIndex = 0;
            const pageSize = 10;
            this.paginatedHistory = this.filteredHistory.slice(startIndex, startIndex + pageSize);
            return;
        }

        const startIndex = this.paginator.pageIndex * this.paginator.pageSize;
        const endIndex = startIndex + this.paginator.pageSize;
        this.paginatedHistory = this.filteredHistory.slice(startIndex, endIndex);
    }

    private getChangedFields(tx_change: string): string[] {
        if (!tx_change) {
            return ['Unknown change'];
        }
        const fields = tx_change.split(',').map(f => f.trim());
        return fields.map(field => this.fieldMappings[field] || field);
    }

    getInitials(email: string): string {
        if (!email) return '?';
        return email.substring(0, 2).toUpperCase();
    }
}
